import { userErrorMessage, type StudentProblemSnapshot } from '@wx-weiqi/contracts';
import { ChevronLeftIcon, ChevronRightIcon, Cross2Icon, ReloadIcon } from '@radix-ui/react-icons';
import { useEffect, useId, useRef, useState } from 'react';
import type {
  StudentPracticeAssignment,
  StudentPracticeClient,
} from '../services/student-practice-client';
import { BoardSurface } from './BoardSurface';
import './student-practice-questions.css';

export function StudentPracticeQuestions({
  client,
  disabled,
  onSelect,
}: {
  readonly client: StudentPracticeClient;
  readonly disabled: boolean;
  readonly onSelect: (problem: StudentProblemSnapshot) => void;
}) {
  const [menu, setMenu] = useState<'sources' | 'assignments' | null>(null);
  const [dialog, setDialog] = useState<'problems' | 'unavailable' | null>(null);
  const [assignments, setAssignments] = useState<readonly StudentPracticeAssignment[]>([]);
  const [assignment, setAssignment] = useState<StudentPracticeAssignment | null>(null);
  const [problems, setProblems] = useState<readonly StudentProblemSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const request = useRef(0);
  const active = useRef(true);
  const menuId = useId();
  const headingId = useId();

  const close = (restoreFocus = true) => {
    request.current += 1;
    setMenu(null);
    setDialog(null);
    setLoading(false);
    setError('');
    if (restoreFocus) trigger.current?.focus();
  };

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      request.current += 1;
    };
  }, [client]);

  useEffect(() => {
    if (!menu) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) close(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [menu]);

  useEffect(() => {
    if (!dialog) return;
    const element = dialogRef.current;
    element?.showModal();
    const timer = dialog === 'unavailable' ? setTimeout(() => close(), 3000) : null;
    return () => {
      if (timer !== null) clearTimeout(timer);
      element?.close();
      if (trigger.current?.isConnected) trigger.current.focus();
    };
  }, [dialog]);

  const loadAssignments = async () => {
    const current = ++request.current;
    setMenu('assignments');
    setDialog(null);
    setLoading(true);
    setError('');
    setAssignments([]);
    try {
      const result = await client.listAssignments();
      if (!active.current || request.current !== current) return;
      setAssignments(result);
      setLoading(false);
    } catch (reason: unknown) {
      if (!active.current || request.current !== current) return;
      setError(userErrorMessage(reason, '作业列表加载失败，请重试。'));
      setLoading(false);
    }
  };

  const loadProblems = async (selected: StudentPracticeAssignment) => {
    const current = ++request.current;
    setAssignment(selected);
    setMenu(null);
    setDialog('problems');
    setLoading(true);
    setError('');
    setProblems([]);
    try {
      const result = await client.loadProblems(selected.assignmentId);
      if (!active.current || request.current !== current) return;
      setProblems(result);
      setLoading(false);
    } catch (reason: unknown) {
      if (!active.current || request.current !== current) return;
      setError(userErrorMessage(reason, '作业题目加载失败，请重试。'));
      setLoading(false);
    }
  };

  return (
    <div
      ref={root}
      className="student-practice-questions"
      onBlur={(event) => {
        if (menu && !event.currentTarget.contains(event.relatedTarget)) close(false);
      }}
      onKeyDown={(event) => {
        if (!menu) return;
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        }
      }}
    >
      <button
        type="button"
        ref={trigger}
        className="student-practice-questions__trigger"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={menu !== null}
        aria-controls={menu ? menuId : undefined}
        onClick={() => {
          if (menu) close();
          else {
            setError('');
            setMenu('sources');
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            setMenu('sources');
          }
        }}
      >
        我的题目
      </button>

      {menu ? (
        <div
          ref={menuRef}
          id={menuId}
          className="student-practice-questions__menu"
          role="menu"
          aria-label={menu === 'sources' ? '我的题目来源' : '我的作业'}
          onKeyDown={(event) => {
            const items = Array.from(
              event.currentTarget.querySelectorAll<HTMLButtonElement>(
                '[role="menuitem"]:not(:disabled)',
              ),
            );
            const index = items.indexOf(document.activeElement as HTMLButtonElement);
            const next =
              event.key === 'ArrowDown'
                ? (index + 1) % items.length
                : event.key === 'ArrowUp'
                  ? (index + items.length - 1) % items.length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? items.length - 1
                      : null;
            if (next !== null) {
              event.preventDefault();
              items[next]?.focus();
            }
          }}
        >
          {menu === 'sources' ? (
            <>
              <button type="button" role="menuitem" onClick={() => void loadAssignments()}>
                <span>作业</span>
                <ChevronRightIcon aria-hidden="true" />
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  request.current += 1;
                  setMenu(null);
                  setDialog('unavailable');
                }}
              >
                <span>题书</span>
                <ChevronRightIcon aria-hidden="true" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                className="student-practice-questions__back"
                onClick={() => {
                  request.current += 1;
                  setMenu('sources');
                  setError('');
                }}
              >
                <ChevronLeftIcon aria-hidden="true" />
                <span>我的作业</span>
              </button>
              {loading ? (
                <p role="status">正在加载作业…</p>
              ) : error ? (
                <div className="student-practice-questions__error" role="alert">
                  <p>{error}</p>
                  <button type="button" role="menuitem" onClick={() => void loadAssignments()}>
                    <ReloadIcon aria-hidden="true" />
                    重试
                  </button>
                </div>
              ) : assignments.length === 0 ? (
                <p role="status">暂无作业</p>
              ) : (
                <div className="student-practice-questions__assignments">
                  {assignments.map((item) => (
                    <button
                      key={item.assignmentId}
                      type="button"
                      role="menuitem"
                      onClick={() => void loadProblems(item)}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {dialog ? (
        <dialog
          ref={dialogRef}
          className={`student-practice-questions__dialog${dialog === 'unavailable' ? ' student-practice-questions__dialog--unavailable' : ''}`}
          aria-labelledby={headingId}
          onCancel={(event) => {
            event.preventDefault();
            close();
          }}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <header className="student-practice-questions__heading">
            <h2 id={headingId}>{dialog === 'unavailable' ? '功能暂未开启' : assignment?.title}</h2>
            <button
              type="button"
              className="student-practice-questions__close"
              aria-label="关闭我的题目"
              onClick={() => close()}
            >
              <Cross2Icon aria-hidden="true" />
            </button>
          </header>
          {dialog === 'problems' ? (
            <div className="student-practice-questions__content" aria-busy={loading}>
              {loading ? (
                <p className="student-practice-questions__status" role="status">
                  正在加载题目…
                </p>
              ) : error ? (
                <div className="student-practice-questions__error" role="alert">
                  <p>{error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      if (assignment) void loadProblems(assignment);
                    }}
                  >
                    <ReloadIcon aria-hidden="true" />
                    重新加载
                  </button>
                </div>
              ) : problems.length === 0 ? (
                <p className="student-practice-questions__status" role="status">
                  这份作业暂无可用题目。
                </p>
              ) : (
                <ul className="student-practice-questions__grid" aria-label="作业题目">
                  {problems.map((problem, index) => (
                    <li key={problem.problemId}>
                      <button
                        type="button"
                        className="student-practice-questions__problem"
                        disabled={disabled}
                        aria-label={`载入第 ${index + 1} 题：${problem.title}`}
                        onClick={() => {
                          if (disabled) return;
                          onSelect(problem);
                          close();
                        }}
                      >
                        <span className="student-practice-questions__preview" aria-hidden="true">
                          <BoardSurface
                            boardAppearance="wood"
                            stoneAppearance="shell"
                            size={problem.initialPosition.size}
                            stones={problem.initialPosition.stones}
                            initialAnnotations={problem.initialAnnotations ?? []}
                            lastMove={null}
                          />
                        </span>
                        <span className="student-practice-questions__problem-name">
                          <strong>第 {index + 1} 题</strong>
                          <span>
                            {problem.initialPosition.currentPlayer === 'WHITE' ? '白先' : '黑先'}
                          </span>
                        </span>
                        <span className="student-practice-questions__problem-title">
                          {problem.title}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </dialog>
      ) : null}
    </div>
  );
}
